import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
} from 'recharts';
import {
  Building2, FileText, FlaskConical, Power, ShieldAlert, Maximize2, Minimize2,
  Loader2, AlertTriangle, MapPin, Crosshair, Sun, Moon,
} from 'lucide-react';
import { API } from '../contexts/AuthContext';
import ModonLogo from '../components/ModonLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';

const C = { blue: '#2167a0', blueDeep: '#0b2a4d', green: '#6cc24a', greenLight: '#8ed26f', info: '#38bdf8' };
const RISK = { high: '#FF4D4F', medium: '#FFA726', low: '#52c41a' };
// GHS-inspired severity colors for hazard classification bars (severe = red).
const HAZ_COLORS = {
  explosive: '#E53935',
  toxic: '#C62828',
  flammable: '#FB8C00',
  oxidizer: '#FDD835',
  corrosive: '#00ACC1',
  'health-hazard': '#D81B60',
  environment: '#43A047',
};
const REFRESH_MS = 30 * 60 * 1000; // 30 minutes
const MAP_CENTER = [23.8859, 45.0792];

// Dual-theme crystal-glass tokens — MODON teal/navy + signature green (4K-grade depth).
const THEMES = {
  dark: {
    bg: 'radial-gradient(135% 115% at 82% 4%, #2a6ea0 0%, #1a4e7e 22%, #103a63 46%, #0a2747 72%, #051428 100%)',
    glass: 'relative overflow-hidden bg-gradient-to-b from-[#1b517f]/55 to-[#0b2a4d]/35 backdrop-blur-2xl border border-white/12 shadow-[0_24px_64px_-18px_rgba(0,0,0,0.65),inset_0_1px_1.5px_rgba(255,255,255,0.16)]',
    text: '#ffffff', sub: 'rgba(214,232,246,0.64)', faint: 'rgba(214,232,246,0.46)',
    title: '#9fe080', accent: '#6cc24a', accentLight: '#b7ec92', sheen: 'rgba(255,255,255,0.45)',
    numGradient: 'bg-gradient-to-b from-white via-[#eaf3fb] to-[#9ec3e0] bg-clip-text text-transparent',
    iconBox: 'border border-white/10 bg-gradient-to-br from-white/15 to-white/[0.02]',
    rowBg: 'bg-white/[0.05] border border-white/10',
    grid: 'rgba(255,255,255,0.06)', axis: 'rgba(214,232,246,0.72)', barLabel: '#ffffff',
    donutStroke: '#0a2747', emptyFill: 'rgba(255,255,255,0.08)',
    gridLine: 'rgba(120,170,220,0.06)',
    orbTeal: 0.42, orbGreen: 0.18,
    mapVignette: 'inset 0 0 90px rgba(5,18,36,0.85)',
    ctrlBorder: 'border-white/12', ctrlText: 'text-white/80 hover:text-white',
    badge: 'bg-white/5 border border-white/10',
  },
  light: {
    bg: 'radial-gradient(135% 115% at 82% 4%, #eef7f8 0%, #e3eff1 45%, #d6e7ea 100%)',
    glass: 'relative overflow-hidden bg-gradient-to-b from-white/85 to-white/60 backdrop-blur-2xl border border-white/80 shadow-[0_22px_56px_-18px_rgba(11,42,77,0.24),inset_0_1px_1.5px_rgba(255,255,255,0.95)]',
    text: '#0b2a4d', sub: 'rgba(11,42,77,0.62)', faint: 'rgba(11,42,77,0.5)',
    title: '#3f8f2a', accent: '#4a9e2e', accentLight: '#5fae3e', sheen: 'rgba(255,255,255,0.9)',
    numGradient: 'bg-gradient-to-b from-[#0b2a4d] to-[#1c5a92] bg-clip-text text-transparent',
    iconBox: 'border border-[#0b2a4d]/10 bg-gradient-to-br from-white to-white/50',
    rowBg: 'bg-[#0b2a4d]/[0.04] border border-[#0b2a4d]/10',
    grid: 'rgba(11,42,77,0.08)', axis: 'rgba(11,42,77,0.75)', barLabel: '#0b2a4d',
    donutStroke: '#ffffff', emptyFill: 'rgba(11,42,77,0.08)',
    gridLine: 'rgba(11,42,77,0.05)',
    orbTeal: 0.16, orbGreen: 0.12,
    mapVignette: 'inset 0 0 60px rgba(11,42,77,0.2)',
    ctrlBorder: 'border-[#0b2a4d]/15', ctrlText: 'text-[#0b2a4d]/80 hover:text-[#0b2a4d]',
    badge: 'bg-[#0b2a4d]/5 border border-[#0b2a4d]/10',
  },
};

function AnimatedNumber({ value = 0, locale = 'ar-SA' }) {
  const [n, setN] = useState(0);
  const fromRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    const to = Number(value) || 0;
    const dur = 1400;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span>{n.toLocaleString(locale)}</span>;
}

function Kpi({ icon: Icon, label, value, color, locale, delay = 0, tt }) {
  return (
    <div className={`tv-fade-up ${tt.glass} rounded-3xl p-5 xl:p-6`} style={{ animationDelay: `${delay}ms` }} data-testid={`tv-kpi-${label}`}>
      <div className="pointer-events-none absolute inset-x-6 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${tt.sheen}, transparent)` }} />
      <div className="pointer-events-none absolute -top-12 ltr:-left-12 rtl:-right-12 w-40 h-40 rounded-full blur-3xl opacity-25" style={{ background: color }} />
      <div className="relative flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className={`text-5xl xl:text-7xl font-semibold tracking-tighter tabular-nums leading-none ${tt.numGradient}`}>
            <AnimatedNumber value={value} locale={locale} />
          </div>
          <div className="text-xs xl:text-sm font-semibold mt-2 uppercase tracking-[0.18em]" style={{ color: tt.title }}>{label}</div>
        </div>
        <div className={`w-16 h-16 xl:w-[72px] xl:h-[72px] rounded-2xl flex items-center justify-center shrink-0 ${tt.iconBox} shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]`}>
          <Icon size={30} style={{ color }} />
        </div>
      </div>
      <div className="pointer-events-none absolute bottom-0 inset-x-0 h-[3px]" style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />
    </div>
  );
}

function Panel({ title, children, className = '', testid, delay = 0, tt }) {
  return (
    <div className={`tv-fade-up ${tt.glass} rounded-[1.75rem] p-4 xl:p-5 flex flex-col ${className}`} style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
      {/* industrial HUD corner brackets */}
      <span className="pointer-events-none absolute top-2.5 left-2.5 w-4 h-4 border-t border-l rounded-tl-md opacity-50" style={{ borderColor: tt.accent }} />
      <span className="pointer-events-none absolute top-2.5 right-2.5 w-4 h-4 border-t border-r rounded-tr-md opacity-50" style={{ borderColor: tt.accent }} />
      <span className="pointer-events-none absolute bottom-2.5 left-2.5 w-4 h-4 border-b border-l rounded-bl-md opacity-50" style={{ borderColor: tt.accent }} />
      <span className="pointer-events-none absolute bottom-2.5 right-2.5 w-4 h-4 border-b border-r rounded-br-md opacity-50" style={{ borderColor: tt.accent }} />
      <div className="pointer-events-none absolute inset-x-5 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${tt.accent}, transparent)` }} />
      <h3 className="text-center font-bold text-base xl:text-xl tracking-wide" style={{ color: tt.title }}>{title}</h3>
      <div className="mx-auto mt-1.5 mb-3 h-px w-14 rounded-full opacity-70" style={{ background: `linear-gradient(90deg, transparent, ${tt.accent}, transparent)` }} />
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

export default function ExecutiveDashboard() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const locale = isEn ? 'en-US' : 'ar-SA';
  const dir = isEn ? 'ltr' : 'rtl';
  const [stats, setStats] = useState(null);
  const [topRisk, setTopRisk] = useState([]);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(null);
  const [clock, setClock] = useState(new Date());
  const [isFs, setIsFs] = useState(false);
  const [mapInst, setMapInst] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('tv_theme') || 'dark');
  const isDark = theme === 'dark';
  const tt = THEMES[theme];
  const toggleTheme = () => {
    const nx = isDark ? 'light' : 'dark';
    setTheme(nx);
    localStorage.setItem('tv_theme', nx);
  };

  const flyToFactory = useCallback((lat, lng) => mapInst?.flyTo([lat, lng], 16, { duration: 1.6 }), [mapInst]);
  const resetMapView = useCallback(() => mapInst?.flyTo(MAP_CENTER, 5, { duration: 1.2 }), [mapInst]);

  const load = useCallback(async () => {
    try {
      const st = await axios.get(`${API}/public/tv-status`);
      if (!st.data?.enabled) { setEnabled(false); setLoading(false); return; }
      setEnabled(true);
      const [s, tr, mp] = await Promise.all([
        axios.get(`${API}/public/stats`),
        axios.get(`${API}/public/top-risk?limit=8`),
        axios.get(`${API}/public/map-factories`),
      ]);
      setStats(s.data); setTopRisk(tr.data || []); setPoints(mp.data || []);
    } catch (e) {
      console.warn('[ExecutiveDashboard] load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const refresh = setInterval(load, REFRESH_MS);
    const tickClock = setInterval(() => setClock(new Date()), 1000);
    return () => { clearInterval(refresh); clearInterval(tickClock); };
  }, [load]);

  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleFs = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen?.();
  };

  const activeFactories = stats?.totalFactories || 0;
  const lang = i18n.language;

  const { riskData, riskTotal, donutData, donutPad } = useMemo(() => {
    const rd = [
      { name: t('tv.high'), value: stats?.highRiskFactories || 0, key: 'high' },
      { name: t('tv.medium'), value: stats?.mediumRiskFactories || 0, key: 'medium' },
      { name: t('tv.low'), value: stats?.lowRiskFactories || 0, key: 'low' },
    ];
    const total = rd.reduce((a, b) => a + b.value, 0);
    const nz = rd.filter((d) => d.value > 0);
    return {
      riskData: rd, riskTotal: total,
      donutData: nz.length ? nz : [{ name: '—', value: 1, key: 'empty' }],
      donutPad: nz.length > 1 ? 4 : 0,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, lang]);

  const hazardData = useMemo(() => {
    const labels = {
      toxic: t('tv.hazToxic'), flammable: t('tv.hazFlammable'), corrosive: t('tv.hazCorrosive'),
      oxidizer: t('tv.hazOxidizer'), explosive: t('tv.hazExplosive'), environment: t('tv.hazEnvironment'),
      'health-hazard': t('tv.hazHealth'),
    };
    return Object.entries(stats?.hazardCounts || {})
      .map(([k, v]) => ({ name: labels[k] || k, value: v, key: k }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, lang]);

  const tickerItems = useMemo(() => (
    topRisk.length
      ? topRisk.map((r) => `${r.factoryName} — ${r.level ? t(`tv.${r.level}`) : ''} (${r.score})`)
      : [t('tv.noData')]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [topRisk, lang]);

  // Digital clock — numerals follow the selected language (Arabic-Indic in AR, Latin in EN).
  const timeParts = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).formatToParts(clock);
  const getPart = (type) => timeParts.find((p) => p.type === type)?.value || '';
  const hh = getPart('hour'), mm = getPart('minute'), ss = getPart('second'), ap = getPart('dayPeriod');
  const dateStr = clock.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div dir={dir} className="min-h-screen w-full relative overflow-hidden flex flex-col" style={{ background: tt.bg, color: tt.text }} data-testid="executive-dashboard">
      {/* Ambient background system */}
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(circle at 50% -8%, rgba(21,90,108,0.25) 0%, transparent 55%)' }} />
      <div className="pointer-events-none absolute top-[-12%] ltr:left-[-6%] rtl:right-[-6%] w-[42vw] h-[42vw] rounded-full" style={{ background: C.blue, filter: 'blur(150px)', mixBlendMode: 'screen', opacity: tt.orbTeal }} />
      <div className="pointer-events-none absolute bottom-[-12%] ltr:right-[-6%] rtl:left-[-6%] w-[42vw] h-[42vw] rounded-full" style={{ background: C.green, filter: 'blur(150px)', mixBlendMode: 'screen', opacity: tt.orbGreen }} />
      {/* engineering blueprint grid — MODON industrial identity */}
      <div className="pointer-events-none absolute inset-0" style={{
        backgroundImage: `linear-gradient(${tt.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${tt.gridLine} 1px, transparent 1px)`,
        backgroundSize: '54px 54px',
        maskImage: 'radial-gradient(ellipse 85% 75% at 50% 38%, #000 25%, transparent 82%)',
        WebkitMaskImage: 'radial-gradient(ellipse 85% 75% at 50% 38%, #000 25%, transparent 82%)',
      }} />
      {/* faint MODON watermark */}
      <div className="pointer-events-none absolute bottom-[-4%] ltr:left-[-3%] rtl:right-[-3%] opacity-[0.05]">
        <ModonLogo size={520} framed={false} />
      </div>

      {/* Header — floating glass pill */}
      <div className="relative z-10 px-4 xl:px-7 pt-4 xl:pt-5">
        <header className={`${tt.glass} rounded-full h-[68px] xl:h-20 px-5 xl:px-7 flex items-center justify-between`}>
          <div className="flex items-center gap-3 xl:gap-4">
            <div className="bg-white/95 rounded-2xl p-1.5 shrink-0"><ModonLogo size={46} /></div>
            <div>
              <h1 className="text-lg xl:text-3xl font-semibold tracking-tight" style={{ color: tt.title }} data-testid="tv-header-title">{t('tv.title')}</h1>
              <p className="text-[10px] xl:text-sm font-medium tracking-wide" style={{ color: tt.sub }}>{t('tv.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 xl:gap-4">
            <div className="hidden md:flex flex-col items-end ltr:pr-1 rtl:pl-1" data-testid="tv-clock">
              <div className="flex items-baseline gap-0.5 font-mono leading-none" dir="ltr">
                <span className="text-3xl xl:text-[3rem] font-semibold tabular-nums tracking-tight" style={{ color: tt.text, textShadow: isDark ? '0 0 24px rgba(108,194,74,0.2)' : 'none' }}>{hh}</span>
                <span className="text-3xl xl:text-[3rem] font-semibold tv-colon-blink" style={{ color: C.green }}>:</span>
                <span className="text-3xl xl:text-[3rem] font-semibold tabular-nums tracking-tight" style={{ color: tt.text, textShadow: isDark ? '0 0 24px rgba(108,194,74,0.2)' : 'none' }}>{mm}</span>
                <span className="text-base xl:text-2xl font-medium tabular-nums ms-0.5" style={{ color: C.greenLight }}>{ss}</span>
                {ap && (
                  <span className="ms-1.5 px-2 py-1 rounded-lg text-[10px] xl:text-xs font-extrabold tracking-[0.15em] self-center" style={{ background: `${C.green}1f`, color: C.green, border: `1px solid ${C.green}44` }}>{ap}</span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2.5">
                <span className="h-[3px] w-6 rounded-full" style={{ background: `linear-gradient(90deg, ${C.green}, transparent)` }} />
                <span className="text-[11px] xl:text-sm font-semibold tracking-wide" style={{ color: tt.sub }}>{dateStr}</span>
              </div>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs xl:text-sm font-bold ${tt.badge}`}>
              <span className="w-2.5 h-2.5 rounded-full tv-live-dot" style={{ background: RISK.low, boxShadow: `0 0 12px ${RISK.low}` }} />
              <span style={{ color: RISK.low }}>{t('tv.liveUpdate')}</span>
            </div>
            <button onClick={toggleTheme} data-testid="tv-theme-toggle-btn" title={isDark ? t('tv.lightMode') : t('tv.darkMode')}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all hover:bg-black/5 border ${tt.ctrlBorder} ${tt.ctrlText}`}>
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button onClick={toggleFs} data-testid="tv-fullscreen-btn" title={isFs ? t('tv.exitFullscreen') : t('tv.fullscreen')}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all hover:bg-black/5 border ${tt.ctrlBorder} ${tt.ctrlText}`}>
              {isFs ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <LanguageSwitcher variant={isDark ? 'dark' : 'light'} />
          </div>
        </header>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center relative z-10">
          <Loader2 className="animate-spin" size={48} style={{ color: C.green }} />
        </div>
      ) : enabled === false ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 relative z-10" data-testid="tv-disabled-screen">
          <div className={`${tt.glass} w-24 h-24 rounded-[1.75rem] flex items-center justify-center mb-6`}>
            <ShieldAlert size={44} style={{ color: RISK.high }} />
          </div>
          <h2 className="text-3xl xl:text-5xl font-light mb-3" style={{ color: tt.title }}>{t('tv.disabledTitle')}</h2>
          <p className="text-base xl:text-xl max-w-xl" style={{ color: tt.sub }}>{t('tv.disabledMsg')}</p>
        </div>
      ) : (
        <main className="flex-1 min-h-0 flex flex-col gap-4 xl:gap-5 px-4 xl:px-7 py-4 xl:py-5 relative z-10">
          {/* KPI row */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 xl:gap-5">
            <Kpi icon={Building2} label={t('tv.totalFactories')} value={stats?.totalFactories} color={C.info} locale={locale} delay={0} tt={tt} />
            <Kpi icon={Power} label={t('tv.activeFactories')} value={activeFactories} color={RISK.low} locale={locale} delay={90} tt={tt} />
            <Kpi icon={FileText} label={t('tv.totalSDS')} value={stats?.totalSDS} color={C.green} locale={locale} delay={180} tt={tt} />
            <Kpi icon={FlaskConical} label={t('tv.totalChemicals')} value={stats?.totalChemicals} color={RISK.medium} locale={locale} delay={270} tt={tt} />
          </div>

          {/* Content grid */}
          <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-12 gap-4 xl:gap-5">
            {/* Risk donut */}
            <Panel title={t('tv.riskDistribution')} className="xl:col-span-3" testid="tv-risk-panel" delay={340} tt={tt}>
              <div className="flex flex-col h-full min-h-0">
                <div className="relative w-full flex-1 min-h-[160px]">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={donutData} dataKey="value" nameKey="name" innerRadius="60%" outerRadius="92%" paddingAngle={donutPad}
                        stroke={donutData.length > 1 ? tt.donutStroke : 'none'} strokeWidth={donutData.length > 1 ? 3 : 0} startAngle={90} endAngle={-270} isAnimationActive={false}>
                        {donutData.map((d) => <Cell key={d.key} fill={d.key === 'empty' ? tt.emptyFill : RISK[d.key]} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="text-4xl xl:text-6xl font-light tabular-nums">{riskTotal.toLocaleString(locale)}</div>
                    <div className="text-xs font-semibold uppercase tracking-widest mt-1" style={{ color: tt.sub }}>{t('tv.factory')}</div>
                  </div>
                </div>
                <div className="shrink-0 flex justify-center items-center gap-3 xl:gap-4 mt-3 pt-1 flex-wrap">
                  {riskData.map((d) => (
                    <div key={d.key} className="flex items-center gap-1.5 text-xs xl:text-sm font-semibold whitespace-nowrap">
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: RISK[d.key], boxShadow: `0 0 8px ${RISK[d.key]}88` }} />
                      {d.name} <span className="tabular-nums" style={{ color: tt.sub }}>{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>

            {/* Map */}
            <Panel title={t('tv.factoriesMap')} className="xl:col-span-5" testid="tv-map-panel" delay={400} tt={tt}>
              <div className="w-full h-full min-h-[220px] rounded-2xl overflow-hidden border border-black/10 relative shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                <div className="absolute inset-0 pointer-events-none z-[450]" style={{ boxShadow: tt.mapVignette }} />
                <button onClick={resetMapView} data-testid="tv-map-reset-btn"
                  className="absolute top-3 ltr:right-3 rtl:left-3 z-[500] flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white bg-[#0b2a4d]/85 backdrop-blur-md border border-[#6cc24a]/45 hover:bg-[#0b2a4d] transition-colors">
                  <Crosshair size={14} style={{ color: C.greenLight }} /> {t('tv.resetView')}
                </button>
                <MapContainer center={MAP_CENTER} zoom={5} scrollWheelZoom={false} dragging={true} ref={setMapInst}
                  style={{ height: '100%', width: '100%', background: '#0b1b2e' }} attributionControl={false}>
                  <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" maxZoom={19} />
                  {points.map((p) => (
                    <CircleMarker key={p.id} center={[p.lat, p.lng]} radius={9}
                      eventHandlers={{ click: () => flyToFactory(p.lat, p.lng) }}
                      pathOptions={{ color: '#fff', weight: 2, fillColor: RISK[p.riskLevel] || '#64748b', fillOpacity: 0.95, className: 'cursor-pointer' }}>
                      <Tooltip direction="top"><div className="text-xs"><b>{p.factoryName}</b> — {p.riskLevel ? t(`tv.${p.riskLevel}`) : p.riskLabel}</div></Tooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
            </Panel>

            {/* Top risk list */}
            <Panel title={t('tv.topRisk')} className="xl:col-span-4" testid="tv-toprisk-panel" delay={460} tt={tt}>
              <div className="h-full overflow-hidden flex flex-col gap-2.5">
                {topRisk.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center text-sm" style={{ color: tt.faint }}>{t('tv.noData')}</div>
                ) : topRisk.slice(0, 6).map((r, i) => (
                  <div key={r.user_id} className={`flex items-center gap-3 rounded-2xl px-3 py-2 ${tt.rowBg} backdrop-blur-sm`} data-testid={`tv-toprisk-row-${i}`}>
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center font-bold text-sm shrink-0 border border-[#6cc24a]/35"
                      style={{ background: `${C.green}26`, color: tt.title }}>{i + 1}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate text-sm xl:text-base">{r.factoryName}</div>
                      <div className="text-[11px] truncate" style={{ color: tt.faint }}><MapPin size={10} className="inline mx-0.5" />{r.city || '—'} • {r.chemicalsCount} {t('tv.material')}</div>
                    </div>
                    <div className="px-2.5 py-1 rounded-lg text-xs font-bold tabular-nums shrink-0 flex items-center gap-1 border"
                      style={{ background: `${RISK[r.level] || RISK.high}1f`, color: RISK[r.level] || RISK.high, borderColor: `${RISK[r.level] || RISK.high}55` }}>
                      <ShieldAlert size={12} /> {r.score}
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          {/* Hazard bar */}
          <Panel title={t('tv.hazardCategories')} className="h-[150px] xl:h-[176px]" testid="tv-hazard-panel" delay={520} tt={tt}>
            {hazardData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm" style={{ color: tt.faint }}>{t('tv.noData')}</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hazardData} margin={{ top: 16, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={tt.grid} vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: tt.axis, fontSize: 12, fontWeight: 700 }} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={56} isAnimationActive={false}>
                    <LabelList dataKey="value" position="top" fill={tt.barLabel} fontSize={13} fontWeight={800} />
                    {hazardData.map((d) => <Cell key={d.key} fill={HAZ_COLORS[d.key] || C.green} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </main>
      )}

      {/* Ticker */}
      {enabled !== false && (
      <footer className="relative z-10 overflow-hidden tv-marquee-wrap border-t border-[#6cc24a]/35 bg-[#0b2a4d] shadow-[0_-8px_32px_rgba(0,0,0,0.4)]" data-testid="tv-ticker">
        <div className="flex items-stretch">
          <div className="shrink-0 flex items-center gap-2 px-5 font-bold text-xs xl:text-sm uppercase tracking-[0.15em] text-white" style={{ background: RISK.high }}>
            <AlertTriangle size={16} /> {t('tv.alert')}
          </div>
          <div className="flex-1 overflow-hidden py-2.5">
            <div className="tv-marquee-track items-center gap-12 text-sm xl:text-lg font-medium tracking-wide" style={{ color: C.greenLight }}>
              {[0, 1].map((dup) => (
                <span key={dup} className="inline-flex items-center gap-12">
                  {tickerItems.map((it, i) => (
                    <span key={i} className="inline-flex items-center gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: RISK.high }} /> {it}
                    </span>
                  ))}
                </span>
              ))}
            </div>
          </div>
        </div>
      </footer>
      )}
    </div>
  );
}
